import { Proto } from "../../../pojo/Proto";
import { S25WsNode } from "../../../pojo/S25WsNode";
import { Workflow } from "../../workflow/service/Workflow";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { ReportBreakdown, ReportSeries } from "../../../standalone/report-dashboard/report.types";

export namespace Budget {
    import NumericalBoolean = Proto.NumericalBoolean;
    import ServiceStatus = Proto.ServiceStatus;
    import ISODateString = Proto.ISODateString;
    import NumericalString = Proto.NumericalString;
    import WorkflowStatus = Workflow.WorkflowStatus;
    import WorkflowSubmissionEntry = Workflow.WorkflowSubmissionEntry;
    import WorkflowStage = Workflow.WorkflowStage;

    export const BUDGET_ITEM_TYPE_ID = 12_000;
    export const BUDGET_PLAN_OBJECT_TYPE = Workflow.BUDGET_PLAN_OBJECT_TYPE;
    export const BUDGET_PLAN_ITEM_TYPE_ID = 12_005;

    /** Rule field type IDs for budget workflow submission entries (align with RuleFieldTypes.Budget). */
    export const WORKFLOW_SUBMISSION_FIELD_TYPES = {
        fiscalYear: 12_004,
        budgetPlanType: 12_005,
        budgetPlanName: 12_006,
        budgetPlanAllocatedAmount: 12_007,
        budgetPlanStartDate: 12_008,
        budgetPlanEndDate: 12_009,
    } as const;

    // Dropdown item with date range for budget selection
    export type BudgetDropdownItem = DropDownItem & {
        startDate?: ISODateString;
        endDate?: ISODateString;
    };

    // Base budget item interface extending S25WsNode
    export interface BaseBudgetItem extends S25WsNode {
        budgetId: number;
        name: string;
        description?: string;
        amount: number;
        currency: string;
        createUser: string;
        createDate: ISODateString;
        lastModUser?: string;
        lastModDate?: ISODateString;
        comments?: string;
    }

    // Fiscal Year - for defining fiscal year periods
    export interface FiscalYear extends S25WsNode {
        fiscalYearId: number;
        name: string;
        description?: string;
        startDate: ISODateString;
        endDate: ISODateString;
        createUser?: string;
        createDt?: ISODateString;
        lastModUser?: string;
        lastModDate?: ISODateString;
    }

    /** ANG-6292: Per-org budget export payload from GET /budget/export.json */
    export interface OrgBudgetExport {
        organizationId: number;
        organizationName?: string;
        orgBudgets: OrgBudget[];
    }

    /** ANG-6292: Budget export payload from GET or POST /budget/export.json */
    export interface BulkBudgetExport {
        exports?: OrgBudgetExport[];
        err_msg?: string;
        err_code?: string;
    }

    // Organization Budget - represents the overall budget allocation for an organization
    export interface OrgBudget extends BaseBudgetItem {
        organizationId: number;
        organizationName: string;
        fiscalYearId: number;
        fiscalYearName?: string;
        fiscalYear?: FiscalYear;
        startDate: ISODateString;
        endDate: ISODateString;
        totalAllocated: number;
        totalSpent: number;
        totalCommitted: number;
        remainingBalance: number;
        workflowProcessId?: number;
        workflowProcessName?: string;
        budgetPlans?: BudgetPlan[];
        categories?: BudgetCategory[];
    }

    // Budget Plan - represents a planned spending allocation
    export interface BudgetPlan extends BaseBudgetItem {
        budgetPlanId: number;
        planType: BudgetPlanType | string;
        startDate: ISODateString;
        endDate: ISODateString;
        allocatedAmount: number | { source: string; parsedValue: number };
        spentAmount: number | { source: string; parsedValue: number };
        committedAmount: number | { source: string; parsedValue: number };
        remainingAmount?: number;
        organizationName?: string; // Populated from parent budget's organization
        budgetLineItems: BudgetLineItem[];

        // Workflow fields (from Workflow.WorkflowItem)
        workflowProcessId?: number;
        workflowProcessInstanceId?: number;
        currentWorkflowStageId?: number;
        currentWorkflowStage?: WorkflowStage;
        contId?: number;
        todoId?: number;
        workflowStatus?: WorkflowStatus; // undefined = draft
        requestedChanges?: string;
        submissionDate?: ISODateString;
        submitted?: boolean;

        // Workflow submission data
        workflowSubmissionList?: WorkflowSubmissionEntry[];
        itemTypeId?: number;
        itemId?: number;

        // Computed/frontend fields
        active?: boolean;
    }

    // Budget Plan Types
    export enum BudgetPlanType {
        ANNUAL = "annual",
        SEMESTER = "semester",
        QUARTER = "quarter",
        EVENT_SPECIFIC = "event_specific",
        OPERATIONAL = "operational",
        SPECIAL_PROJECT = "special_project",
    }

    export type BudgetPlanTypeOption = {
        itemId: number;
        code: string;
        itemName: string;
    };

    export const BudgetPlanTypes: readonly BudgetPlanTypeOption[] = [
        { itemId: 1, code: "quarterly", itemName: "Quarterly" },
        { itemId: 2, code: "semester", itemName: "Semester" },
        { itemId: 3, code: "annual", itemName: "Annual" },
        { itemId: 4, code: "event", itemName: "Event-Specific" },
        { itemId: 5, code: "special_project", itemName: "Special Project" },
    ];

    export function budgetPlanTypeByCode(planTypeCode: string): BudgetPlanTypeOption | undefined {
        return BudgetPlanTypes.find((planType) => planType.code === planTypeCode);
    }

    export function budgetPlanTypeToDropdownItem(planTypeCode: string): DropDownItem | undefined {
        const planType = budgetPlanTypeByCode(planTypeCode);
        if (!planType) return undefined;
        return {
            itemId: planType.itemId,
            itemName: planType.itemName,
            itemTypeId: BUDGET_PLAN_ITEM_TYPE_ID,
        };
    }

    export function budgetPlanTypeCodeFromDropdownItem(
        dropdownItem: DropDownItem | string | null | undefined,
    ): string | undefined {
        if (!dropdownItem) return undefined;
        if (typeof dropdownItem === "string") return dropdownItem;
        return BudgetPlanTypes.find((planType) => planType.itemId === Number(dropdownItem.itemId))?.code;
    }

    export function budgetPlanTypeDisplayName(rawValue: string | undefined): string | undefined {
        if (!rawValue?.trim()) return undefined;
        const trimmed = rawValue.trim();
        const byCode = budgetPlanTypeByCode(trimmed);
        if (byCode) return byCode.itemName;
        const numericId = Number(trimmed);
        if (Number.isFinite(numericId)) {
            return BudgetPlanTypes.find((planType) => planType.itemId === numericId)?.itemName;
        }
        return trimmed;
    }

    // Budget Category - for categorizing budget items
    export interface BudgetCategory extends S25WsNode {
        categoryId: number;
        name: string;
        description?: string;
        color?: string;
        icon?: string;
        isActive: NumericalBoolean;
        sortOrder: number;
        parentCategoryId?: number;
        subCategories?: BudgetCategory[];
    }

    // Budget Line Item - detailed breakdown within a budget plan
    export interface BudgetLineItem extends BaseBudgetItem {
        budgetPlanId: number;
        lineItemId: number;
        categoryId: number;
        categoryName: string;
        plannedAmount: number;
        actualAmount: number;
        committedAmount: number;
        allocatedAmount?: number;
        spentAmount?: number;
        remainingAmount?: number;
        quantity?: number;
        unitCost?: number;
        vendor?: string;
        purchaseOrderNumber?: string;
        receiptNumber?: string;
    }

    // Budget Summary - for dashboard displays
    export interface BudgetSummary {
        budgetId?: number;
        orgBudgetId?: number;
        organizationId: number;
        organizationName: string;
        totalAllocated: number;
        totalSpent: number;
        totalCommitted: number;
        remainingBalance: number;
        utilizationPercentage: number;
        budgetPlansCount: number;
        activePlansCount: number;
        fiscalYearId: number;
        fiscalYear?: FiscalYear;
        approvedPlanAllocated?: number;
        pendingPlanAllocated?: number;
        approvedPlansCount?: number;
        pendingPlansCount?: number;
    }

    // Budget Report Data
    export interface BudgetReportData {
        summary: {
            totalAllocated: number;
            totalSpent: number;
            totalCommitted: number;
            remainingBalance: number;
            transactionCount: number;
        };
        categoryBreakdown: Array<{
            categoryId: number;
            categoryName: string;
            allocated: number;
            spent: number;
            committed: number;
            remaining: number;
        }>;
        monthlySpending: Array<{
            month: string;
            spent: number;
            committed: number;
        }>;
        topVendors: Array<{
            vendor: string;
            totalSpent: number;
            transactionCount: number;
        }>;
        events: Array<{
            eventId: number;
            eventName: string;
            allocated: number;
            spent: number;
            committed: number;
        }>;
    }

    // Budget Settings - configuration for budget management
    export interface BudgetSettings {
        organizationId: number;
        allowOverBudget: boolean;
        overBudgetLimit: number;
        defaultCategories: BudgetCategory[];
        fiscalYearStart: string; // MM-DD format
        currency: string;
        decimalPlaces: number;
        autoCloseInactivePlans: boolean;
        inactivePlanThreshold: number; // days
        notificationSettings: BudgetNotificationSettings;
    }

    // Budget Notification Settings
    export interface BudgetNotificationSettings {
        lowBalanceThreshold: number;
        overBudgetThreshold: number;
        monthlyReport: boolean;
        reportRecipients: string[];
    }

    // Data interfaces for API responses
    export interface OrgBudgetResponse {
        budget: OrgBudget;
        summary: BudgetSummary;
        settings: BudgetSettings;
    }

    export interface BudgetPlanResponse {
        budgetPlan: BudgetPlan;
        orgBudget: OrgBudget;
        categories: BudgetCategory[];
    }

    // ============================================================================
    // ADMIN-SPECIFIC INTERFACES
    // ============================================================================

    export interface AdminBudgetSummary {
        fiscalYear: string;
        totalOrganizations: number;
        totalBudgetsCreated: number;
        totalAllocated: number;
        totalSpent: number;
        totalCommitted: number;
        pendingRequests: number;
        utilizationPercentage: number;
        averageBudgetAmount: number;
    }

    export interface OrganizationBudgetStatus {
        organizationId: number;
        organizationName: string;
        organizationType?: string;
        hasBudget: boolean;
        budgetId?: number;
        allocatedAmount?: number;
        spentAmount?: number;
        committedAmount?: number;
        utilizationPercentage?: number;
        budgetStatus?: "active" | "warning" | "critical" | "expired" | "upcoming";
        workflowProcessId?: number;
        workflowProcessName?: string;
        lastModDate?: ISODateString;
    }

    // ============================================================================
    // ANG-6292: BUDGET REPORTS (POST /budget/reports/breakdowns.json, drilldown.json)
    // ============================================================================

    export type ReportDimension =
        | "ORG_TYPE"
        | "ORG_STANDING"
        | "ORG_CATEGORY"
        | "PERCENT_PLANNED"
        | "PLAN_TYPE"
        | "LINE_ITEM_CATEGORY";

    export type ReportMeasure = "allocated" | "planned" | "spent" | "committed";

    export type ReportMeasures = Record<ReportMeasure, number>;

    /** Row key the server uses for a missing dimension value (label "(None)"). */
    export const REPORT_NONE_KEY = "none";

    export interface ReportFilter {
        fiscalYearIds: number[];
        /** Empty = every dimension. */
        dimensions: ReportDimension[];
        organizationIds?: number[];
        planTypes?: string[];
        categoryIds?: number[];
    }

    export interface ReportSummary {
        totalAllocated: number;
        totalPlanned: number;
        totalSpent: number;
        totalCommitted: number;
        orgBudgetCount: number;
        planCount: number;
        lineItemCount: number;
    }

    export type ReportBreakdownDoc = ReportBreakdown<ReportDimension, ReportMeasure>;

    export interface ReportValidationFields {
        message?: string;
        messageCode?: string;
    }

    export interface ReportResponse extends ReportValidationFields {
        summary: ReportSummary;
        series: ReportSeries[];
        breakdowns: ReportBreakdownDoc[];
    }

    export interface ReportDrilldownRequest extends ReportFilter {
        dimension: ReportDimension;
        key: string;
        seriesKey?: string;
    }

    export type ReportEntityType = "ORG_BUDGET" | "PLAN" | "LINE_ITEM";

    export interface ReportDrilldownRow {
        organizationId: number;
        organizationName: string;
        budgetId: number;
        budgetName: string;
        budgetPlanId?: number;
        planName?: string;
        planType?: string;
        lineItemId?: number;
        lineItemName?: string;
        categoryName?: string;
        fiscalYearName: string;
        seriesKey: string;
        percentPlanned?: number;
        measures: ReportMeasures;
    }

    export interface ReportDrilldownResponse extends ReportValidationFields {
        entityType: ReportEntityType;
        rows: ReportDrilldownRow[];
    }

    export interface BulkBudgetTemplate {
        fiscalYear: string;
        defaultAmount: number;
        currency: string;
        startDate: ISODateString;
        endDate: ISODateString;
        descriptionTemplate: string;
        categories?: BudgetCategory[];
    }

    export function getPlanAllocationSummary(budget: OrgBudget): {
        approvedAllocated: number;
        pendingAllocated: number;
        approvedPlansCount: number;
        pendingPlansCount: number;
    } {
        return (budget.budgetPlans || []).reduce(
            (summary, budgetPlan) => {
                const allocatedAmount =
                    typeof budgetPlan.allocatedAmount === "number"
                        ? budgetPlan.allocatedAmount
                        : budgetPlan.allocatedAmount?.parsedValue || 0;

                if (budgetPlan.workflowStatus === "COMPLETED") {
                    summary.approvedAllocated += allocatedAmount;
                    summary.approvedPlansCount += 1;
                } else if (budgetPlan.workflowStatus === "PENDING" || budgetPlan.workflowStatus === "IN_PROGRESS") {
                    summary.pendingAllocated += allocatedAmount;
                    summary.pendingPlansCount += 1;
                }

                return summary;
            },
            {
                approvedAllocated: 0,
                pendingAllocated: 0,
                approvedPlansCount: 0,
                pendingPlansCount: 0,
            },
        );
    }

    export function getUtilization(budget: OrgBudget): number {
        if (!budget.totalAllocated) return 0;

        const allocationSummary = getPlanAllocationSummary(budget);
        if (budget.budgetPlans?.length) {
            return (allocationSummary.approvedAllocated / budget.totalAllocated) * 100;
        }

        return (((budget.totalSpent || 0) + (budget.totalCommitted || 0)) / budget.totalAllocated) * 100;
    }
}
